<script lang="ts" module>
    export const route = "/settings";
</script>

<script lang="ts">
    import { setBreadcrumbs } from "../lib/components/breadcrumbs.svelte";
    import Sidebar from "../lib/components/sidebar/sidebar.svelte";
    import { resolve } from "../lib/route.svelte.js";
    import { useMemberPermissions, auth, useOrganization, refetchOrganization } from "../lib/auth.svelte.js";
    import * as Table from "../lib/components/ui/table/index.js";
    import * as Avatar from "../lib/components/ui/avatar/index.js";
    import * as Dialog from "../lib/components/ui/dialog/index.js";
    import * as Field from "../lib/components/ui/field/index.js";
    import * as Select from "../lib/components/ui/select/index.js";
    import * as Tabs from "../lib/components/ui/tabs/index.js";
    import * as AlertDialog from "../lib/components/ui/alert-dialog/index.js";
    import { Input } from "../lib/components/ui/input/index.js";
    import { Button } from "../lib/components/ui/button/index.js";
    import { Badge } from "../lib/components/ui/badge/index.js";
    import { TagInput } from "../lib/components/ui/tag-input/index.js";
    import UserPlus from "@lucide/svelte/icons/user-plus";
    import EllipsisVertical from "@lucide/svelte/icons/ellipsis-vertical";
    import Plus from "@lucide/svelte/icons/plus";
    import Save from "@lucide/svelte/icons/save";
    import { m } from "../lib/messages.svelte.js";
    import Date from "../lib/components/common/date.svelte";
    import { toast } from "svelte-sonner";
    import { getClient } from "../lib/utils.js";
    import { assert, definedOrDie, multimapBy, z } from "@repo/utils";

    $effect.pre(() => {
        setBreadcrumbs([{ title: m["settings"](), href: resolve("/settings") }]);
    });

    const organization = useOrganization();
    let domains = $derived(organization.domains);
    let members = $derived(organization.members);
    let teams = $derived(organization.teams ?? []);
    const canListMembers = useMemberPermissions({ member: ["list"] });
    const canCreateMember = useMemberPermissions({ member: ["create"] });
    const canUpdateMember = useMemberPermissions({ member: ["update"] });
    const canManageTeams = useMemberPermissions({ team: ["create"] });
    const canCreateManager = useMemberPermissions({ manager: ["create"] });
    const canUpdateOrganization = useMemberPermissions({ organization: ["update"] });
    let inProgress = $state(false);
    const teamsByUserId = $derived(
        multimapBy(
            organization.teamMembers ?? [],
            (x) => x.userId,
            (x) => x.teamId,
        ),
    );
    const membersCountByTeamId = $derived(
        (organization.teamMembers ?? []).reduce<Record<string, number>>((acc, x) => {
            acc[x.teamId] = (acc[x.teamId] ?? 0) + 1;
            return acc;
        }, {}),
    );
    let memberForm = $state<{
        memberId: string | null;
        userId: string | null;
        firstName: string;
        lastName: string;
        email: string;
        memberRole: "manager" | "member";
        teamIds: string[];
    }>();

    async function saveMember() {
        inProgress = true;
        assert(memberForm);
        try {
            const { memberId, userId } = memberForm;
            if (memberId && userId) {
                await getClient()["Update organization member"]({
                    params: { memberId },
                    jsonBody: {
                        firstName: memberForm.firstName,
                        lastName: memberForm.lastName,
                        memberRole: memberForm.memberRole,
                        teamIds: memberForm.teamIds,
                    },
                });
            } else {
                await getClient()["Invite organization member"]({
                    jsonBody: {
                        firstName: memberForm.firstName,
                        lastName: memberForm.lastName,
                        email: memberForm.email,
                        memberRole: memberForm.memberRole,
                        teamIds: memberForm.teamIds,
                    },
                });
                toast.success(m["Invitation sent successfully"]());
            }
            await refetchOrganization();
        } finally {
            memberForm = undefined;
            inProgress = false;
        }
    }

    async function confirmRemoveMember() {
        inProgress = true;
        assert(memberForm && memberForm.memberId);
        try {
            await auth.organization.removeMember({ memberIdOrEmail: memberForm.memberId });
            await refetchOrganization();
        } finally {
            memberForm = undefined;
            inProgress = false;
        }
    }

    let teamForm = $state<{ id: string | null; name: string }>();

    async function saveTeam() {
        inProgress = true;
        assert(teamForm);
        try {
            if (teamForm.id) {
                await auth.organization.updateTeam({ teamId: teamForm.id, data: { name: teamForm.name } });
            } else {
                await auth.organization.createTeam({ name: teamForm.name });
            }
            await refetchOrganization();
        } finally {
            teamForm = undefined;
            inProgress = false;
        }
    }

    async function confirmDeleteTeam() {
        inProgress = true;
        assert(teamForm && teamForm.id);
        try {
            await auth.organization.removeTeam({ teamId: teamForm.id });
            await refetchOrganization();
        } finally {
            teamForm = undefined;
            inProgress = false;
        }
    }
</script>

<Sidebar>
    {#await canListMembers then canViewMembers}
        {#if canViewMembers}
            <div class="container mx-auto max-w-4xl space-y-6 p-6">
                <Tabs.Root value="members">
                    <Tabs.List>
                        <Tabs.Trigger value="members">{m["Members"]()}</Tabs.Trigger>
                        <Tabs.Trigger value="teams">{m["Teams"]()}</Tabs.Trigger>
                        <Tabs.Trigger value="preferences">{m["Preferences"]()}</Tabs.Trigger>
                    </Tabs.List>

                    <Tabs.Content value="members" class="space-y-4">
                        <div class="flex items-center justify-end">
                            {#await canCreateMember then canCreateMember}
                                {#if canCreateMember}
                                    <Button
                                        onclick={() => {
                                            memberForm = {
                                                userId: null,
                                                memberId: null,
                                                firstName: "",
                                                lastName: "",
                                                email: "",
                                                memberRole: "member",
                                                teamIds: [],
                                            };
                                        }}
                                    >
                                        <UserPlus class="mr-2 h-4 w-4" />
                                        {m["Invite member"]()}
                                    </Button>
                                {/if}
                            {/await}
                        </div>

                        <Table.Root>
                            <Table.Header>
                                <Table.Row>
                                    <Table.Head>{m["Name"]()}</Table.Head>
                                    <Table.Head>{m["Role"]()}</Table.Head>
                                    <Table.Head>{m["Teams"]()}</Table.Head>
                                    <Table.Head>{m["Joined"]()}</Table.Head>
                                    <Table.Head></Table.Head>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {#each members as member (member.id)}
                                    <Table.Row>
                                        <Table.Cell>
                                            <div class="flex items-center gap-3">
                                                <Avatar.Root class="h-9 w-9">
                                                    <Avatar.Image src={member.user.image} alt={member.user.email} />
                                                    <Avatar.Fallback>
                                                        {member.user.name
                                                            ?.split(" ")
                                                            .map((x) => x[0])
                                                            .join("")
                                                            .toUpperCase()}
                                                    </Avatar.Fallback>
                                                </Avatar.Root>
                                                <div>
                                                    <div class="font-medium">{member.user.name}</div>
                                                    <div class="text-muted-foreground text-xs">
                                                        {member.user.email}
                                                    </div>
                                                </div>
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Badge variant={member.role === "member" ? "secondary" : "default"}>
                                                {member.role}
                                            </Badge>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div class="flex flex-wrap gap-1">
                                                {#each teamsByUserId.get(member.userId) ?? [] as teamId (teamId)}
                                                    {@const team = teams.find((t) => t.id === teamId)}
                                                    {#if team}
                                                        <Badge variant="secondary">{team.name}</Badge>
                                                    {/if}
                                                {/each}
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell class="text-muted-foreground">
                                            <Date value={member.createdAt.toISOString()} withTime />
                                        </Table.Cell>
                                        <Table.Cell class="text-muted-foreground text-right">
                                            {#await canUpdateMember then canEdit}
                                                {#if canEdit}
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        onclick={() => {
                                                            memberForm = {
                                                                memberId: member.id,
                                                                userId: member.userId,
                                                                email: member.user.email,
                                                                firstName: member.user.firstName,
                                                                lastName: member.user.lastName,
                                                                memberRole: member.role,
                                                                teamIds: teamsByUserId.get(member.userId) ?? [],
                                                            };
                                                        }}
                                                    >
                                                        <EllipsisVertical />
                                                    </Button>
                                                {/if}
                                            {/await}
                                        </Table.Cell>
                                    </Table.Row>
                                {/each}
                            </Table.Body>
                        </Table.Root>
                    </Tabs.Content>

                    <Tabs.Content value="teams" class="space-y-4">
                        <div class="flex items-center justify-end">
                            {#await canManageTeams then canManage}
                                {#if canManage}
                                    <Button
                                        onclick={() => {
                                            teamForm = { id: null, name: "" };
                                        }}
                                    >
                                        <Plus class="mr-2 h-4 w-4" />
                                        {m["Create team"]()}
                                    </Button>
                                {/if}
                            {/await}
                        </div>

                        {#if teams.length === 0}
                            <p class="text-muted-foreground text-sm">{m["No teams yet"]()}</p>
                        {:else}
                            <Table.Root>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.Head>{m["Name"]()}</Table.Head>
                                        <Table.Head>{m["Members"]()}</Table.Head>
                                        <Table.Head></Table.Head>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {#each teams as team (team.id)}
                                        <Table.Row>
                                            <Table.Cell class="font-medium">{team.name}</Table.Cell>
                                            <Table.Cell class="font-medium"
                                                >{membersCountByTeamId[team.id] ?? 0}</Table.Cell
                                            >
                                            <Table.Cell class="text-muted-foreground text-right">
                                                {#await canManageTeams then canManage}
                                                    {#if canManage}
                                                        <Button
                                                            size="icon"
                                                            variant="ghost"
                                                            onclick={() => {
                                                                teamForm = { id: team.id, name: team.name };
                                                            }}
                                                        >
                                                            <EllipsisVertical />
                                                        </Button>
                                                    {/if}
                                                {/await}
                                            </Table.Cell>
                                        </Table.Row>
                                    {/each}
                                </Table.Body>
                            </Table.Root>
                        {/if}
                    </Tabs.Content>

                    {#await canUpdateOrganization then canUpdateOrganization}
                        {#if canUpdateOrganization}
                            <Tabs.Content value="preferences" class="space-y-4">
                                <div class="flex items-center justify-end">
                                    <Button
                                        disabled={inProgress}
                                        onclick={async () => {
                                            inProgress = true;
                                            try {
                                                await getClient()["Update preferences"]({ jsonBody: { domains } });
                                            } finally {
                                                inProgress = false;
                                            }
                                        }}
                                    >
                                        <Save class="mr-2 h-4 w-4" />
                                        {m["Save"]()}
                                    </Button>
                                </div>
                                <Field.Group>
                                    <Field.Set>
                                        <Field.Field class="w-120">
                                            <Field.Label>{m["Email Domains"]()}</Field.Label>
                                            <TagInput
                                                bind:value={domains}
                                                validate={(x) => {
                                                    const valid = x.filter((x) => z.regexes.domain.exec(x));
                                                    if (valid.length !== x.length) {
                                                        toast.warning(m["Some emails couldn't be added"](), {
                                                            description:
                                                                m[
                                                                    "Entries with invalid email addresses were removed."
                                                                ](),
                                                        });
                                                    }
                                                    return valid;
                                                }}
                                            />
                                        </Field.Field>
                                    </Field.Set>
                                </Field.Group>
                            </Tabs.Content>
                        {/if}
                    {/await}
                </Tabs.Root>
            </div>

            <Dialog.Root
                bind:open={
                    () => !!memberForm,
                    (x) => {
                        if (!x) {
                            memberForm = undefined;
                        }
                    }
                }
            >
                {#if memberForm}
                    <Dialog.Content class="sm:max-w-106.25">
                        <Dialog.Header>
                            <Dialog.Title>
                                {#if memberForm.memberId}
                                    {m["Edit member"]()}
                                {:else}
                                    {m["Invite a new member"]()}
                                {/if}
                            </Dialog.Title>
                        </Dialog.Header>

                        <Field.Set>
                            <Field.Group>
                                <Field.Field>
                                    <Field.Label for="firstName">{m["First Name"]()}</Field.Label>
                                    <Input id="firstName" bind:value={memberForm.firstName} placeholder={m["John"]()} />
                                </Field.Field>
                                <Field.Field>
                                    <Field.Label for="lastName">{m["Last Name"]()}</Field.Label>
                                    <Input id="lastName" bind:value={memberForm.lastName} placeholder={m["Doe"]()} />
                                </Field.Field>
                            </Field.Group>
                            <Field.Group>
                                <Field.Field>
                                    <Field.Label for="email">{m["Email"]()}</Field.Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        bind:value={memberForm.email}
                                        placeholder={m["john@example.com"]()}
                                        disabled={!!memberForm.memberId}
                                    />
                                </Field.Field>
                            </Field.Group>
                            {#await canCreateManager then canInviteManager}
                                {#if canInviteManager}
                                    <Field.Group>
                                        <Field.Field>
                                            <Field.Label>{m["Role"]()}</Field.Label>
                                            <Select.Root
                                                type="single"
                                                value={memberForm.memberRole}
                                                onValueChange={(v) =>
                                                    (definedOrDie(memberForm).memberRole = v as "manager" | "member")}
                                            >
                                                <Select.Trigger class="w-full">
                                                    {memberForm.memberRole === "manager"
                                                        ? m["Manager"]()
                                                        : m["Member"]()}
                                                </Select.Trigger>
                                                <Select.Content>
                                                    <Select.Item value="member">{m["Member"]()}</Select.Item>
                                                    <Select.Item value="manager">{m["Manager"]()}</Select.Item>
                                                </Select.Content>
                                            </Select.Root>
                                        </Field.Field>
                                    </Field.Group>
                                {/if}
                            {/await}
                            {#if teams.length > 0}
                                <Field.Group>
                                    <Field.Field>
                                        <Field.Label>{m["Teams"]()}</Field.Label>
                                        <TagInput
                                            options={teams.map((t) => ({ value: t.id, label: t.name }))}
                                            bind:value={memberForm.teamIds}
                                            placeholder={m["Add team"]()}
                                        />
                                    </Field.Field>
                                </Field.Group>
                            {/if}
                        </Field.Set>

                        <Dialog.Footer>
                            {#if memberForm.memberId}
                                <AlertDialog.Root>
                                    <AlertDialog.Trigger>
                                        {#snippet child({ props })}
                                            <Button {...props} variant="destructive" class="mr-auto">
                                                {m["Delete"]()}
                                            </Button>
                                        {/snippet}
                                    </AlertDialog.Trigger>
                                    <AlertDialog.Content>
                                        <AlertDialog.Header>
                                            <AlertDialog.Title>
                                                {m["Are you sure you want to remove user {email}?"]({
                                                    email: memberForm.email,
                                                })}
                                            </AlertDialog.Title>
                                        </AlertDialog.Header>
                                        <AlertDialog.Footer>
                                            <AlertDialog.Cancel>{m["Cancel"]()}</AlertDialog.Cancel>
                                            <AlertDialog.Action onclick={confirmRemoveMember}>
                                                {m["Confirm"]()}
                                            </AlertDialog.Action>
                                        </AlertDialog.Footer>
                                    </AlertDialog.Content>
                                </AlertDialog.Root>
                            {/if}
                            <Dialog.Close>
                                <Button variant="outline">{m["Cancel"]()}</Button>
                            </Dialog.Close>
                            <Button
                                onclick={saveMember}
                                disabled={inProgress ||
                                    !memberForm.firstName ||
                                    !memberForm.lastName ||
                                    (!memberForm.memberId && !memberForm.email)}
                            >
                                {#if memberForm.memberId}
                                    {m["Save"]()}
                                {:else}
                                    {m["Send Invitation"]()}
                                {/if}
                            </Button>
                        </Dialog.Footer>
                    </Dialog.Content>
                {/if}
            </Dialog.Root>

            <Dialog.Root
                bind:open={
                    () => !!teamForm,
                    (x) => {
                        if (!x) {
                            teamForm = undefined;
                        }
                    }
                }
            >
                {#if teamForm}
                    <Dialog.Content class="sm:max-w-106.25">
                        <Dialog.Header>
                            <Dialog.Title>
                                {teamForm.id ? m["Edit team"]() : m["Create team"]()}
                            </Dialog.Title>
                        </Dialog.Header>
                        <Field.Set>
                            <Field.Group>
                                <Field.Field>
                                    <Field.Label for="teamName">{m["Team name"]()}</Field.Label>
                                    <Input id="teamName" bind:value={teamForm.name} />
                                </Field.Field>
                            </Field.Group>
                        </Field.Set>
                        <Dialog.Footer>
                            {#if teamForm.id}
                                <AlertDialog.Root>
                                    <AlertDialog.Trigger>
                                        {#snippet child({ props })}
                                            <Button {...props} variant="destructive" class="mr-auto">
                                                {m["Delete"]()}
                                            </Button>
                                        {/snippet}
                                    </AlertDialog.Trigger>
                                    <AlertDialog.Content>
                                        <AlertDialog.Header>
                                            <AlertDialog.Title>
                                                {m["Are you sure you want to delete team {name}?"]({
                                                    name: teamForm.name,
                                                })}
                                            </AlertDialog.Title>
                                        </AlertDialog.Header>
                                        <AlertDialog.Footer>
                                            <AlertDialog.Cancel>{m["Cancel"]()}</AlertDialog.Cancel>
                                            <AlertDialog.Action onclick={confirmDeleteTeam}>
                                                {m["Confirm"]()}
                                            </AlertDialog.Action>
                                        </AlertDialog.Footer>
                                    </AlertDialog.Content>
                                </AlertDialog.Root>
                            {/if}
                            <Dialog.Close>
                                <Button variant="outline">{m["Cancel"]()}</Button>
                            </Dialog.Close>
                            <Button onclick={saveTeam} disabled={inProgress || !teamForm.name}>
                                {m["Save"]()}
                            </Button>
                        </Dialog.Footer>
                    </Dialog.Content>
                {/if}
            </Dialog.Root>
        {:else}
            <div class="flex size-full items-center justify-center">
                <p class="text-muted-foreground">
                    {m["You don't have permission to view this page."]()}
                </p>
            </div>
        {/if}
    {/await}
</Sidebar>
